import { useRef, useCallback, useEffect } from 'react';

import { Data3d } from '@store';

import { useElvaVisualizerContext } from './ElvaVisualizerContext';
import { getLockedCameraParams } from './measurementUtils';
import { LOCK_TYPE, ParamAxisLock } from './types';

export const CAMERA_TRANSITION_DURATION_MS = 500;

export type MeasurementFocusParams = {
    axisId: string;
    keypointName: string;
    measurementName: string;
    axisIndex: number;
    lock: ParamAxisLock;
    targetPoints?: string[];
};

type MeasurementFocusHookArgs = {
    activeData: Data3d | null;
    currentFrame: number;
};

export function useMeasurementFocus({ activeData, currentFrame }: MeasurementFocusHookArgs) {
    const {
        currentFocusId,
        setCurrentFocusId,
        setForcedAzimuthalAngle,
        setForcedPolarAngle,
        setCameraMode,
        setIntrinsicCameraParams,
    } = useElvaVisualizerContext();

    const cameraUnlockTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingLockRef = useRef<MeasurementFocusParams | null>(null);

    const clearCameraUnlockTimeout = useCallback(() => {
        if (!cameraUnlockTimeoutRef.current) {
            return;
        }

        clearTimeout(cameraUnlockTimeoutRef.current);
        cameraUnlockTimeoutRef.current = null;
    }, []);

    const resetFocus = useCallback(() => {
        clearCameraUnlockTimeout();
        pendingLockRef.current = null;

        setCameraMode('orbit');
        setIntrinsicCameraParams(null);
        setCurrentFocusId(null);
        setForcedAzimuthalAngle(null);
        setForcedPolarAngle(null);
    }, [
        clearCameraUnlockTimeout,
        setCameraMode,
        setCurrentFocusId,
        setForcedAzimuthalAngle,
        setForcedPolarAngle,
        setIntrinsicCameraParams,
    ]);

    const handleSetMeasurementFocus = useCallback(
        (focus: MeasurementFocusParams) => {
            if (!activeData) {
                return;
            }

            clearCameraUnlockTimeout();

            if (currentFocusId === focus.axisId) {
                resetFocus();
                return;
            }

            if (focus.lock.type === LOCK_TYPE.STATIC) {
                pendingLockRef.current = null;

                setCameraMode('orbit');
                setIntrinsicCameraParams(null);
                setForcedAzimuthalAngle(focus.lock.azimuthal);
                setForcedPolarAngle(focus.lock.polar);
                setCurrentFocusId(focus.axisId);

                cameraUnlockTimeoutRef.current = setTimeout(() => {
                    cameraUnlockTimeoutRef.current = null;
                    setForcedAzimuthalAngle(null);
                    setForcedPolarAngle(null);
                }, CAMERA_TRANSITION_DURATION_MS);

                return;
            }

            const cameraParams = getLockedCameraParams(activeData, currentFrame, focus);

            if (!cameraParams) {
                return;
            }

            pendingLockRef.current = focus;

            setCurrentFocusId(focus.axisId);
            setCameraMode('intrinsic');
            setIntrinsicCameraParams(cameraParams);
        },
        [
            activeData,
            clearCameraUnlockTimeout,
            currentFocusId,
            currentFrame,
            resetFocus,
            setCameraMode,
            setCurrentFocusId,
            setForcedAzimuthalAngle,
            setForcedPolarAngle,
            setIntrinsicCameraParams,
        ],
    );

    useEffect(() => {
        const pendingLock = pendingLockRef.current;

        if (!activeData || !pendingLock) {
            return;
        }

        const cameraParams = getLockedCameraParams(activeData, currentFrame, pendingLock);

        if (cameraParams) {
            setIntrinsicCameraParams(cameraParams);
        }
    }, [activeData, currentFrame, setIntrinsicCameraParams]);

    useEffect(() => {
        return () => {
            clearCameraUnlockTimeout();
        };
    }, [clearCameraUnlockTimeout]);

    return {
        currentFocusId,
        handleSetMeasurementFocus,
        resetFocus,
    };
}
